import React, { useState, useEffect } from "react";
import { Typography, Grid, Box } from "@mui/material";
import axios from "axios";
import moment from "moment";
import { useParams, useNavigate } from "react-router-dom";
import Button from "../Button";
import StarIcon from "@mui/icons-material/Star";
import "./style.css";

export default function JobDetailsFun() {
  const [jobDetails, setJobDetails] = useState({
    title: "",
    description: "",
    jobType: "",
    jobCategory: "",
    location: "",
    emergency: false,
    recurring: false,
    scheduledTime: "",
    jobId: "",
    owner: {
      ratings: 0,
      reviews: ""
    }
  });
  const [jobImage, setJobImage] = useState(null);
  const [showFullImage, setShowFullImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasQuotation, setHasQuotation] = useState(false);

  const navigate = useNavigate();
  const { jobId } = useParams();

  // Fetch job details when the component mounts
  useEffect(() => {
    if (jobId) {
      fetchJobDetails(jobId);
      fetchJobImage(jobId);
      checkQuotationExists(jobId);
    } else {
      setError("No job ID in the URL.");
      setLoading(false);
    }
  }, [jobId]);

  // Function to fetch job details
  const fetchJobDetails = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }
      
      const response = await axios.get(`https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      
      if (response.data) {
        setJobDetails(response.data);
      }
    } catch (err) {
      console.error("Error fetching job details:", err);
      setError("Failed to load job details. " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Function to check if a quotation exists for this job
  const checkQuotationExists = async (id) => {
    try {
      const token = localStorage.getItem("authToken");
      
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }
      
      const response = await axios.get(`https://jobmanagement-497524552274.us-central1.run.app/api/quotations/job/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      
      // If we get a valid response with data, a quotation exists
      if (response.data && Object.keys(response.data).length > 0) {
        setHasQuotation(true);
      }
    } catch (err) {
      // If we get a 404, it means no quotation exists yet
      if (err.response && err.response.status === 404) {
        setHasQuotation(false);
      } else {
        console.error("Error checking quotation status:", err);
      }
    }
  };

  // Function to fetch job image
  const fetchJobImage = async (id) => {
    try {
      const token = localStorage.getItem("authToken");
      
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }
      
      const response = await axios.get(`https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${id}/image`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob' // Important for image data
      });
      
      if (response.data) {
        // Create a URL for the blob data
        const imageUrl = URL.createObjectURL(response.data);
        setJobImage(imageUrl);
      }
    } catch (err) {
      console.error("Error fetching job image:", err);
      // We don't set error state here to avoid blocking the whole component if only the image fails
    }
  };

  // Event handler for "View Image" button
  const handleViewImage = () => {
    if (jobImage) {
      setShowFullImage(!showFullImage);
    } else {
      fetchJobImage(jobId);
    }
  };

  // Format the scheduledTime to display in a readable format
  const formatDate = (dateTime) => {
    if (!dateTime) return "";
    return moment(dateTime).format("D MMM YYYY");
  };

  const formatTime = (dateTime) => {
    if (!dateTime) return "";
    return moment(dateTime).format("LT");
  };

  // Event handler for "Go to Quote Calculation" button
  const handleGoToQuote = () => {
    if (jobId && !hasQuotation) {
      navigate(`/quote-calculation/${jobId}`);
    } else {
      console.log("Job ID is missing or quotation already exists.");
    }
  };

  if (loading) {
    return <Typography>Loading job details...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Grid container padding="3%">
      <Grid container>
        <Grid item xs={12} sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" sx={{ mr: 2 }}>
            Job Details
          </Typography>
          <Typography variant="h4" sx={{ color: "text.secondary" }}>
            - {jobId}
          </Typography>
        </Grid>
        
        {/* Basic Job Details Section */}
        <Grid item xs={12} sx={{ p: 2 }} className="main-box-viewJobs-card">
          <Grid>
            <div className="labelDivCls">
              <div className="labelCls">Job Title</div>
              <div className="valueCls">{jobDetails.title}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Job Type</div>
              <div className="valueCls">{jobDetails.jobType}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Job Category</div>
              <div className="valueCls">{jobDetails.jobCategory}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Description</div>
              <div className="valueCls">{jobDetails.description}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Location</div>
              <div className="valueCls">{jobDetails.location}</div>
            </div>
          </Grid>
        </Grid>

        {/* Emergency and Scheduling Details Section */}
        <Grid item xs={12} sx={{ p: 2 }} className="main-box-viewJobs-card">
          <Grid>
            <div className="labelDivCls">
              <div className="labelCls">Is this an emergency?</div>
              <div className="valueCls">{jobDetails.emergency ? "Yes" : "No"}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Job Date</div>
              <div className="valueCls">{formatDate(jobDetails.scheduledTime)}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Job Time</div>
              <div className="valueCls">{formatTime(jobDetails.scheduledTime)}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Is this a recurring issue?</div>
              <div className="valueCls">{jobDetails.recurring ? "Yes" : "No"}</div>
            </div>
          </Grid>
        </Grid>

        {/* Homeowner Rating and Reviews Section */}
        <Grid item xs={12} sx={{ p: 2 }} className="main-box-viewJobs-card">
          <Typography variant="h6" sx={{ mb: 2 }}>
            Homeowner Information
          </Typography>
          
          <div className="labelDivCls">
            <div className="labelCls">Homeowner Rating</div>
            <div className="valueCls">
              <StarIcon style={{ fontSize: 18, color: "#0d4f57", verticalAlign: "middle" }} />
              {jobDetails.owner?.ratings || 0}
            </div>
          </div>
          
          <div className="labelDivCls">
            <div className="labelCls">Homeowner Reviews</div>
            <div className="valueCls">{jobDetails.owner?.reviews || "No reviews available"}</div>
          </div>
        </Grid>

        {/* Job Image Section */}
        <Grid item xs={12} sx={{ p: 2 }} className="main-box-viewJobs-card">
          <Typography variant="h6" sx={{ mb: 2 }}>
            Job Images by HomeOwner
          </Typography>
          
          {jobImage && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <img 
                src={jobImage} 
                alt="Job Image" 
                style={{ 
                  maxWidth: showFullImage ? '100%' : '300px', 
                  maxHeight: showFullImage ? 'auto' : '200px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setShowFullImage(!showFullImage)}
              />
              {showFullImage && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Click the image to resize
                </Typography>
              )}
            </Box>
          )}
          
          {!jobImage && (
            <Typography>No image available for this job</Typography>
          )}
        </Grid>

        {/* Action Buttons Section */}
        <Grid item xs={12} sx={{ p: 2, display: "flex", justifyContent: "space-between" }}>
          <Button 
            name="Go Back" 
            onClick={() => window.history.back()} 
            color="green" 
          />
          
          <Button
            name={hasQuotation ? "Quotation Created" : "Go to Quote Calculation"}
            onClick={handleGoToQuote}
            color={hasQuotation ? "grey" : "green"}
            disabled={hasQuotation}
          />
        </Grid>
        
        {hasQuotation && (
          <Grid item xs={12} sx={{ textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
              A quotation has already been created for this job.
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}